import React, { useEffect, useRef, useState } from 'react';
import { GOOGLE_MAPS_API_KEY } from '@partsbadger/utils';
let googleMapsLoader = null;
function loadGoogleMapsPlaces(apiKey) {
    if (window.google?.maps?.importLibrary) {
        return window.google.maps.importLibrary('places');
    }
    if (!googleMapsLoader) {
        const loader = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&v=weekly`;
            script.async = true;
            script.onload = () => {
                const importLibrary = window.google?.maps?.importLibrary;
                if (importLibrary) {
                    resolve(importLibrary('places'));
                }
                else {
                    reject(new Error('Google Maps Places API did not load.'));
                }
            };
            script.onerror = () => reject(new Error('Google Maps Places API could not load.'));
            document.head.appendChild(script);
        });
        googleMapsLoader = loader.catch(error => {
            googleMapsLoader = null;
            throw error;
        });
        return loader;
    }
    return googleMapsLoader;
}
function extractFromAddress(components, type) {
    return components.find(component => component.types.includes(type)) ?? null;
}
function toAddressItem(components) {
    const postCode = extractFromAddress(components, 'postal_code');
    const streetNumber = extractFromAddress(components, 'street_number');
    const street = extractFromAddress(components, 'route');
    const town = extractFromAddress(components, 'locality');
    const state = extractFromAddress(components, 'administrative_area_level_1');
    const country = extractFromAddress(components, 'country');
    return {
        street: `${streetNumber?.longText ?? ''} ${street?.longText ?? ''}`.trim(),
        city: town?.longText ?? '',
        state: {
            key: state?.shortText ?? '',
            label: state?.longText ?? '',
            value: state?.shortText ?? '',
        },
        country: {
            key: country?.shortText ?? '',
            label: country?.longText ?? '',
            value: country?.shortText ?? '',
        },
        zip_code: postCode?.longText ?? '',
    };
}
function applyAutocompleteTheme(element) {
    const darkMode = document.documentElement.classList.contains('dark');
    element.style.colorScheme = darkMode ? 'dark' : 'light';
    element.style.backgroundColor = darkMode ? '#141414' : '#ffffff';
    element.style.color = darkMode ? '#ffffff' : '#000000';
    element.style.border = darkMode ? '1px solid #434343' : '1px solid #d9d9d9';
    element.style.borderRadius = '2px';
    element.style.fontFamily = 'inherit';
}
const AutoCompleteAddressInput = (props) => {
    const autocompleteRef = useRef(null);
    const [unavailable, setUnavailable] = useState(false);
    const { onAutoFill, onChange } = props;
    useEffect(() => {
        let isMounted = true;
        let autocompleteElement = null;
        const themeObserver = new MutationObserver(() => {
            if (autocompleteElement) {
                applyAutocompleteTheme(autocompleteElement);
            }
        });
        themeObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });
        const handleSelectAddress = async (event) => {
            const placePrediction = event.placePrediction;
            if (!placePrediction) {
                return;
            }
            try {
                const place = placePrediction.toPlace();
                await place.fetchFields({ fields: ['addressComponents'] });
                if (!isMounted || !place.addressComponents) {
                    return;
                }
                const addressItem = toAddressItem(place.addressComponents);
                onChange?.(addressItem.street);
                onAutoFill?.(addressItem);
            }
            catch {
                if (isMounted) {
                    setUnavailable(true);
                }
            }
        };
        loadGoogleMapsPlaces(GOOGLE_MAPS_API_KEY)
            .then(({ PlaceAutocompleteElement }) => {
            if (!isMounted || !autocompleteRef.current) {
                return;
            }
            autocompleteElement = new PlaceAutocompleteElement();
            autocompleteElement.setAttribute('data-testid', 'google-places-autocomplete');
            autocompleteElement.setAttribute('placeholder', 'Type Street or P.O. Box');
            autocompleteElement.style.display = 'block';
            autocompleteElement.style.width = '100%';
            applyAutocompleteTheme(autocompleteElement);
            autocompleteElement.addEventListener('gmp-select', handleSelectAddress);
            autocompleteRef.current.replaceChildren(autocompleteElement);
        })
            .catch(() => {
            if (isMounted) {
                setUnavailable(true);
            }
        });
        return () => {
            isMounted = false;
            themeObserver.disconnect();
            autocompleteElement?.removeEventListener('gmp-select', handleSelectAddress);
        };
    }, [onAutoFill, onChange]);
    if (!GOOGLE_MAPS_API_KEY?.trim() || unavailable) {
        return React.createElement("div", { role: "status" }, "Address autocomplete is unavailable. Enter the address manually.");
    }
    return React.createElement("div", { ref: autocompleteRef });
};
export default AutoCompleteAddressInput;
